import type { CalendarEvent } from '@momomail/shared';

/**
 * Date math for the calendar view, all in the viewer's local time zone with plain Date.
 * Day keys are local 'YYYY-MM-DD'. Event times follow the backend: all-day start / end are 'YYYY-MM-DD' (end exclusive), others UTC ISO.
 */

export type DayKey = string;

const pad = (n: number) => String(n).padStart(2, '0');

export function dayKey(d: Date): DayKey {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local midnight of a day key. */
export function parseDay(key: DayKey): Date {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

export function addDays(key: DayKey, days: number): DayKey {
  const d = parseDay(key);
  d.setDate(d.getDate() + days);
  return dayKey(d);
}

export function addMonths(key: DayKey, months: number): DayKey {
  const d = parseDay(key);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  // Keep the day where possible (Jan 31 + 1 month → Feb 28)
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return dayKey(d);
}

export function todayKey(): DayKey {
  return dayKey(new Date());
}

/** The first day of the week containing key (weekStart 0 = Sunday, 1 = Monday). */
export function startOfWeek(key: DayKey, weekStart: number): DayKey {
  const d = parseDay(key);
  return addDays(key, -((d.getDay() - weekStart + 7) % 7));
}

/** The 7 days of the week containing key. */
export function weekDays(key: DayKey, weekStart: number): DayKey[] {
  const first = startOfWeek(key, weekStart);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
}

/** Six full weeks covering the month of key (the month grid always has 42 cells so it doesn't jump in height). */
export function monthGrid(key: DayKey, weekStart: number): DayKey[] {
  const first = `${key.slice(0, 7)}-01`;
  const start = startOfWeek(first, weekStart);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/** First and last day (inclusive) an event occupies, locally. All-day ends are exclusive in the data. */
export function eventDays(e: Pick<CalendarEvent, 'allDay' | 'start' | 'end'>): { first: DayKey; last: DayKey } {
  if (e.allDay) {
    const last = addDays(e.end, -1);
    return { first: e.start, last: last < e.start ? e.start : last };
  }
  const s = new Date(e.start);
  const end = new Date(e.end);
  // An event ending exactly at midnight doesn't occupy the next day
  const last = new Date(Math.max(+s, +end - 1));
  return { first: dayKey(s), last: dayKey(last) };
}

/** Shown on top of a day / in the all-day row: all-day events and timed ones spanning several days. */
export function isAllDayLike(e: Pick<CalendarEvent, 'allDay' | 'start' | 'end'>): boolean {
  if (e.allDay) return true;
  const { first, last } = eventDays(e);
  return first !== last;
}

/** Events by the days they occupy (within the given days), all-day / multi-day first, then by start time. */
export function eventsByDay(events: CalendarEvent[], days: DayKey[]): Map<DayKey, CalendarEvent[]> {
  const map = new Map<DayKey, CalendarEvent[]>(days.map((d) => [d, []]));
  if (!days.length) return map;
  const lo = days[0]!;
  const hi = days[days.length - 1]!;
  for (const e of events) {
    const { first, last } = eventDays(e);
    if (last < lo || first > hi) continue;
    for (let d = first < lo ? lo : first; d <= last && d <= hi; d = addDays(d, 1)) map.get(d)?.push(e);
  }
  for (const list of map.values()) list.sort(compareEvents);
  return map;
}

export function compareEvents(a: CalendarEvent, b: CalendarEvent): number {
  const al = isAllDayLike(a);
  const bl = isAllDayLike(b);
  if (al !== bl) return al ? -1 : 1;
  const as = a.allDay ? +parseDay(a.start) : +new Date(a.start);
  const bs = b.allDay ? +parseDay(b.start) : +new Date(b.start);
  return as - bs || a.title.localeCompare(b.title);
}

/** A timed event's minutes within one day (clipped to 0–1440). */
export function minutesOn(e: Pick<CalendarEvent, 'start' | 'end'>, day: DayKey): { from: number; to: number } {
  const base = +parseDay(day);
  const next = +parseDay(addDays(day, 1));
  const s = Math.max(+new Date(e.start), base);
  const end = Math.min(Math.max(+new Date(e.end), s), next);
  const from = Math.round((s - base) / 60_000);
  const span = (next - base) / 60_000;
  const to = Math.round((end - base) / 60_000);
  return { from: Math.min(from, span), to: Math.min(Math.max(to, from), span) };
}

export interface Placed<T> {
  item: T;
  from: number;
  to: number;
  /** Column within its group of overlapping events, and how many columns the group has. */
  col: number;
  cols: number;
}

/**
 * Lay out timed events of one day side by side where they overlap: each group of mutually overlapping events gets as many columns
 * as it needs at most, and each event takes the first free column. Very short events count as at least minHeight minutes.
 */
export function layoutDay<T>(items: { item: T; from: number; to: number }[], minLength = 20): Placed<T>[] {
  const sorted = [...items].sort((a, b) => a.from - b.from || b.to - a.to);
  const out: Placed<T>[] = [];
  let group: Placed<T>[] = [];
  let groupEnd = -1;
  const columnsEnd: number[] = [];
  const flush = () => {
    const cols = Math.max(1, ...group.map((g) => g.col + 1));
    for (const g of group) g.cols = cols;
    out.push(...group);
    group = [];
    columnsEnd.length = 0;
  };
  for (const it of sorted) {
    const end = Math.max(it.to, it.from + minLength);
    if (group.length && it.from >= groupEnd) flush();
    let col = columnsEnd.findIndex((e) => e <= it.from);
    if (col < 0) col = columnsEnd.length;
    columnsEnd[col] = end;
    group.push({ ...it, col, cols: 1 });
    groupEnd = Math.max(groupEnd, end);
  }
  if (group.length) flush();
  return out;
}

/** Local 'HH:MM' of an ISO time. */
export function clockOf(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Local 'YYYY-MM-DDTHH:MM' (for datetime-like inputs) ↔ ISO. */
export function localInput(iso: string): { date: DayKey; time: string } {
  const d = new Date(iso);
  return { date: dayKey(d), time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}

export function fromLocal(date: DayKey, time: string): string {
  const [h, m] = time.split(':').map(Number) as [number, number];
  const d = parseDay(date);
  d.setHours(h || 0, m || 0, 0, 0);
  return d.toISOString();
}
